/**
 * useAddContent Hook
 *
 * Manages state and validation logic for the video upload flow.
 * Orchestrates data across steps (video selection → preview → description).
 * Matches Flutter _onVideoSelected validation from pick_video_screen.dart.
 */

import { useState, useCallback, useMemo } from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { MathUtils } from '@/domain/utils/mathUtils';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { AddContentFlowType } from '@/domain/enums/publish/addContentFlowType';
import { MediaType } from '@/domain/enums/mediaType';
import type { DescriptionResult } from '@/domain/entities/publish/descriptionResult';
import type { ContentSettingsResult } from '@/domain/entities/publish/contentSettingsResult';
import type { VideoMeta } from '@/domain/entities/publish/videoMeta';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';

export type { VideoMeta };

/**
 * Identity of the media being drafted in the current add-content flow.
 * Generated up front so downstream steps (e.g. Link Content) can reference it
 * before the media is persisted. Shaped to accept additional fields as the
 * flow evolves (description, schedule, contentLink, ...).
 */
export interface UserMediaData {
  mediaId: string;
}

/**
 * The flow discriminators for this hook, as one named options object rather than
 * trailing positional flags (Options-Object Params convention) — mirroring
 * `AddContentSheetShowOptions`, the already-converted surface that sits directly
 * in front of this hook, and the sibling `useAddContentSubmitSeams`. Named fields
 * keep call sites self-describing, make the three interchangeable booleans
 * non-transposable, and let every future flow variant add a field rather than
 * another positional slot.
 *
 * At most one discriminator should be set; see the `flowType` resolution below
 * for the precedence applied when more than one somehow is.
 */
export interface UseAddContentParams {
  /** Presence drives the chat-media flow (Flutter `chat_screen.dart:719-725`). */
  chatUsersInfo?: ChatUsersInfoEntity;
  /** `true` drives the community-media flow (images + videos). */
  isCommunityFlow?: boolean;
  /** `true` drives the feedback flow (image-only, collect-not-send). */
  isFeedbackFlow?: boolean;
  /** `true` drives the profile-image flow (image-only, collect-not-send). */
  isProfileImageFlow?: boolean;
  /**
   * `true` drives the comment-reply flow — the FULL five-step user flow with a
   * "Reply comment" picker title, not a trimmed message flow. `comment`
   * deliberately inherits every `user`-flow default: the drop step stays
   * video-only (`resolveDropStepConfig` falls through to `{}`), the duration
   * band stays `CONTENT_MIN/MAX_SECONDS` (`isMessageVideoFlow` stays false), and
   * all five steps render (`isMessageFlow` stays false). Matches Flutter, whose
   * comment reply runs the ordinary publish chain
   * (`add_content_screen.dart:678-706`).
   */
  isCommentReplyFlow?: boolean;
}

export const useAddContent = ({
  chatUsersInfo,
  isCommunityFlow,
  isFeedbackFlow,
  isProfileImageFlow,
  isCommentReplyFlow,
}: UseAddContentParams = {}) => {
  const { l10n } = useLocale();

  /**
   * Which publish flow this sheet is driving. Presence of `chatUsersInfo` is
   * the chat discriminator (matches Flutter `chat_screen.dart:719-725`);
   * `isCommunityFlow` is the community discriminator; `isFeedbackFlow` is the
   * feedback discriminator (image-only, collect-not-send — Flutter
   * `in_app_feedback_screen.dart:73-81`); `isProfileImageFlow` is the
   * profile-image discriminator (the same image-only, collect-not-send shape as
   * feedback — Flutter `edit_user_profile_screen.dart:270-286` picks with
   * `allowVideo: false` / `allowAudio: false` / `returnLocalMedia: true` /
   * `uploadOnly: true`); `isCommentReplyFlow` is the comment-reply
   * discriminator (Flutter `comments_bottom_sheet.dart:915-923`). The five
   * discriminators are mutually exclusive (chat wins, then community, then
   * feedback, then profileImage, then commentReply if more than one is somehow
   * set); every other entry point resolves to `user`. Only `user`, `chat`,
   * `community`, `feedback`, `profileImage`, and `comment` are wired — the
   * remaining enum members are reserved for future flows.
   *
   * `comment` sits LAST, immediately before the `user` fallback, because it is
   * the one non-`user` member that behaves like `user` everywhere except the
   * step-1 title: it deliberately inherits every `user`-flow default — video-only
   * drop (`resolveDropStepConfig` falls through to `{}`), the
   * `CONTENT_MIN/MAX_SECONDS` duration band (`isMessageVideoFlow` stays false)
   * and all five steps (`isMessageFlow` stays false) — matching Flutter's comment
   * reply running the ordinary publish chain (`add_content_screen.dart:678-706`).
   */
  const flowType = useMemo<AddContentFlowType>(
    () => {
      if (chatUsersInfo) return AddContentFlowType.chat;
      if (isCommunityFlow) return AddContentFlowType.community;
      if (isFeedbackFlow) return AddContentFlowType.feedback;
      if (isProfileImageFlow) return AddContentFlowType.profileImage;
      if (isCommentReplyFlow) return AddContentFlowType.comment;
      return AddContentFlowType.user;
    },
    [chatUsersInfo, isCommunityFlow, isFeedbackFlow, isProfileImageFlow, isCommentReplyFlow],
  );

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [videoMeta, setVideoMeta] = useState<VideoMeta | null>(null);
  const [mediaType, setMediaType] = useState<MediaType | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(false);
  const [descriptionResult, setDescriptionResult] = useState<DescriptionResult | null>(null);
  const [contentSettings, setContentSettings] = useState<ContentSettingsResult | null>(null);
  const [userMediaData] = useState<UserMediaData>(() => ({ mediaId: generateUuidV1() }));

  const clearPendingNavigation = useCallback(() => {
    setPendingNavigation(false);
  }, []);

  const handleFileDrop = useCallback(async (file: File) => {
    setValidationError(null);
    setVideoMeta(null);
    setMediaType(null);

    // Chat + community both accept BOTH images and videos (community video is
    // the go-live switch, this story); the user flow stays video-only
    // (byte-for-byte unchanged). Feedback is IMAGE-ONLY (Flutter
    // in_app_feedback_screen.dart:73-81 picks from gallery images only, never
    // video) — it joins the image branch but is deliberately excluded from
    // `isVideo` so a dropped video routes to the unsupported-format message.
    // ProfileImage is image-only too, stated in Flutter as explicit picker flags
    // rather than an images-only gallery call: `allowVideo: false` +
    // `allowAudio: false` (edit_user_profile_screen.dart:275-276). Excluding it
    // from `isVideo` is the web enforcement of those two flags.
    // Community, feedback AND profileImage IMAGES validate against the NARROWER
    // community list (no HEIC/HEIF). Community and feedback raw-upload the source
    // file with no client-side re-encode — community to the frozen ffmpeg image CF
    // (can't decode HEIC/HEIF), and feedback to a plain bucket whose web read side
    // renders screenshots in an `<img>` (can't decode HEIC/HEIF on Chrome/Firefox →
    // broken image). ProfileImage reaches the same precondition by a different
    // route: it DOES re-encode, but through `memoryStorage.compressImage`, which is
    // a `<canvas>` + `canvas.toBlob('image/jpeg')` decode (memoryStorage.ts:85-136)
    // that Chrome/Firefox cannot feed a HEIC/HEIF source. That failure is silent
    // rather than loud — `compressImage` returns null and `downloadUserProfileImage`
    // falls back to `compressedBlob || imageBlob` (memoryStorage.ts:197), so the RAW
    // undecoded HEIC would upload labelled `contentType: 'image/jpeg'`
    // (cloudStorageService.ts) and render broken for every viewer on BOTH clients;
    // the read side is an `<img>`/`CachedImage` with the same constraint. Same
    // precondition → same narrow predicate (finding_1). This catches a
    // programmatically-injected HEIC too, routing it to the unsupported-format
    // branch below. Chat keeps the full image list — its client-transcode path
    // re-encodes to JPEG before upload.
    const isCommunity = flowType === AddContentFlowType.community;
    const isChat = flowType === AddContentFlowType.chat;
    const isFeedback = flowType === AddContentFlowType.feedback;
    const isProfileImage = flowType === AddContentFlowType.profileImage;
    const isImage = MediaUtils.isImageFile(file) && (
      (isCommunity || isFeedback || isProfileImage)
        ? MediaUtils.isSupportedCommunityImageFormat(file)
        : isChat
          ? MediaUtils.isSupportedImageFormat(file)
          : false
    );
    // Videos: chat AND community both accept supported videos — community video
    // send ships this story (Flutter lib/data/repository/medias_repository.dart:257-281).
    // Feedback and profileImage are image-only, so a video never counts as a
    // valid drop for either.
    const isVideo =
      !isFeedback && !isProfileImage
      && MediaUtils.isVideoFile(file) && MediaUtils.isSupportedVideoFormat(file);

    if (!isVideo && !isImage) {
      // All message flows now accept videos, so an unsupported drop uses the
      // combined video-format message (the chat behaviour). Community mirrors
      // chat here — it accepts both images and videos (this story).
      setValidationError(l10n.publish.unsupportedFormatMessage);
      return;
    }

    setIsProcessing(true);
    setSelectedFile(file);

    try {
      if (isImage) {
        // Images have no duration; skip duration validation entirely.
        // Deliberate divergence from Flutter's _setImageOrientation
        // (add_content_screen.dart:385-401): NO client-side EXIF orientation bake /
        // re-encode — task-prompt line 13 forbids client resize/compress; the raw file
        // uploads as-is. Browsers report EXIF-oriented naturalWidth/Height, so the
        // computed aspect ratio matches the displayed orientation. Known limitation:
        // the transcode CF (frozen, task-prompt line 27) does not apply EXIF rotation,
        // so an EXIF-rotated source may produce rotated renditions.
        const resolution = await MediaUtils.getImageFileResolution(file);

        setMediaType(MediaType.IMAGE);
        setVideoMeta({
          duration: 0,
          width: resolution.width,
          height: resolution.height,
          thumbnailUrl: URL.createObjectURL(file),
        });
        setIsProcessing(false);
        setPendingNavigation(true);
        return;
      }

      const [duration, resolution, thumbnailUrl] = await Promise.all([
        MediaUtils.getVideoFileDuration(file),
        MediaUtils.getVideoFileResolution(file),
        MediaUtils.generateVideoFileThumbnail(file),
      ]);

      // Chat AND community videos validate against the same chat duration band
      // (5 s – 5 min) — task-prompt line 2 "same 5 minute long video limit";
      // the user flow keeps contentMinSeconds/contentMaxSeconds.
      const isMessageVideoFlow = flowType === AddContentFlowType.chat || isCommunity;
      const minSeconds = isMessageVideoFlow
        ? remoteConfigService.chatContentMinSeconds
        : remoteConfigService.contentMinSeconds;
      const maxSeconds = isMessageVideoFlow
        ? remoteConfigService.chatContentMaxSeconds
        : remoteConfigService.contentMaxSeconds;

      const hasValidDuration = duration >= minSeconds && duration <= maxSeconds;

      if (!hasValidDuration) {
        const maxMinutes = MathUtils.secondsToMinutesCeil(maxSeconds);
        setValidationError(
          l10n.publish.unsupportedDurationMessage
            .replace('{min}', String(minSeconds))
            .replace('{max}', String(maxMinutes))
        );
        setIsProcessing(false);
        return;
      }

      setMediaType(MediaType.VIDEO);
      setVideoMeta({
        duration,
        width: resolution.width,
        height: resolution.height,
        thumbnailUrl,
      });
      setIsProcessing(false);
      setPendingNavigation(true);
    } catch (error) {
      appLogger.error('Failed to process media file', 'useAddContent.handleFileDrop', { error: error as Error });
      setValidationError(l10n.publish.unsupportedDuration);
      setIsProcessing(false);
    }
  }, [l10n, flowType]);

  const handleVideoConfirm = useCallback(() => {
    // Navigate from video preview (step 2) to description (step 3)
    setPendingNavigation(true);
  }, []);

  const handleDescriptionComplete = useCallback((result: DescriptionResult) => {
    setDescriptionResult(result);
    // Navigate from description (step 3) to content settings (step 4)
    setPendingNavigation(true);
  }, []);

  const handleSettingsComplete = useCallback((result: ContentSettingsResult) => {
    setContentSettings(result);
    setPendingNavigation(true);
  }, []);

  const handleBack = useCallback((currentStep: number) => {
    if (currentStep === 1) {
      // Going back from video preview to drop video
      setSelectedFile(null);
      setVideoMeta(null);
      setMediaType(null);
      setValidationError(null);
    }
  }, []);

  return {
    flowType,
    selectedFile,
    videoMeta,
    mediaType,
    validationError,
    isProcessing,
    pendingNavigation,
    descriptionResult,
    contentSettings,
    userMediaData,
    clearPendingNavigation,
    handleFileDrop,
    handleVideoConfirm,
    handleDescriptionComplete,
    handleSettingsComplete,
    handleBack,
  };
};
